import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useAuth } from '@/context/auth-context';
import {
  getNavigationItemsForRole,
  getSearchableItems,
  type SearchableNavItem,
} from './navigation-items';

export function NavigationSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();

  // Get searchable items based on user role
  const searchableItems = useMemo(() => {
    const items = getNavigationItemsForRole(user?.role);
    return getSearchableItems(items);
  }, [user?.role]);

  // Group items by section
  const groupedItems = useMemo(() => {
    const groups: Record<string, SearchableNavItem[]> = {};
    for (const item of searchableItems) {
      const section = item.section || 'Other';
      if (!groups[section]) {
        groups[section] = [];
      }
      groups[section].push(item);
    }
    return groups;
  }, [searchableItems]);

  // Handle keyboard shortcut
  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  // Handle item selection
  const handleSelect = useCallback(
    (to: string) => {
      setOpen(false);
      navigate(to);
    },
    [navigate]
  );

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        className="relative flex items-center gap-2 pl-10 pr-4 py-2 rounded-md bg-secondary/50 text-sm text-muted-foreground hover:bg-secondary/70 transition-colors w-64"
      >
        <Search className="absolute left-3 h-4 w-4" />
        <span>Search menu...</span>
        <kbd className="pointer-events-none absolute right-2 hidden h-5 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">&#8984;</span>K
        </kbd>
      </button>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="Search pages..." />
        <CommandList>
          <CommandEmpty>No results found.</CommandEmpty>
          {Object.entries(groupedItems).map(([section, items]) => (
            <CommandGroup key={section} heading={section}>
              {items.map((item) => (
                <CommandItem
                  key={item.to}
                  value={`${item.label} ${item.hint || ''} ${item.parentLabel || ''}`}
                  onSelect={() => handleSelect(item.to)}
                  className="cursor-pointer"
                >
                  {item.icon && <item.icon className="mr-2 h-4 w-4" />}
                  <div className="flex flex-col">
                    <span>
                      {item.parentLabel && (
                        <span className="text-muted-foreground">{item.parentLabel} &gt; </span>
                      )}
                      {item.label}
                    </span>
                    {item.hint && (
                      <span className="text-xs text-muted-foreground">{item.hint}</span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          ))}
        </CommandList>
      </CommandDialog>
    </>
  );
}
